import type { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckmarkOutline } from '@carbon/icons-react';

import type { LockedFeatureKey } from '@maestra/core/constants/bloqueios';
import type { BillingCycle } from '@maestra/core/store/slices/subscription';
import { useAjustarPlano } from '../../../logica/dinheiro/useAjustarPlano';
import { useDesbloqueio } from '../../../logica/dinheiro/useDesbloqueio';
import { useModuloTravado, type PapelNoBloqueio } from '../../../logica/dinheiro/useModuloTravado';
import { useCheckoutDaAssinatura } from '../../../logica/dinheiro/useCheckoutDaAssinatura';
import { usePagamentoPix } from '../../../logica/dinheiro/usePagamentoPix';
import { usePagamentos } from '../../../logica/dinheiro/usePagamentos';
import { usePainelDaAssinatura } from '../../../logica/dinheiro/usePainelDaAssinatura';
import { usePlanos } from '../../../logica/dinheiro/usePlanos';
import { AjustarPlano } from './AjustarPlano';
import { Checkout } from './Checkout';
import { Desbloqueio } from './Desbloqueio';
import { ModuloTravado } from './ModuloTravado';
import { Sucesso } from './Sucesso';
import { PagamentoPix } from './PagamentoPix';
import { Pagamentos } from './Pagamentos';
import { PainelDaAssinatura, Planos } from './Planos';

// AS TELAS DO DINHEIRO LIGADAS: a lógica (src/logica/dinheiro) de um lado, o desenho do outro.

/** O checkout ligado. O desbloqueio de um perfil monta este mesmo, com o destino dele. */
export const CheckoutLigado: FC<{
  aoAtivar: () => void;
  aoIrParaPagamento: () => void;
  aoJaSerAssinante: () => void;
  cicloInicial?: BillingCycle;
  perfisIniciais?: number;
  gbIniciais?: number;
  titulo?: string;
  rodape?: ReactNode;
}> = ({ titulo, rodape, ...destinos }) => <Checkout c={useCheckoutDaAssinatura(destinos)} titulo={titulo} rodape={rodape} />;

const PainelLigado: FC = () => <PainelDaAssinatura p={usePainelDaAssinatura()} />;

export const PlanosLigados: FC = () => {
  const p = usePlanos();
  return (
    <Planos
      p={p}
      painel={p.estado === 'painel' ? <PainelLigado /> : null}
      checkout={p.estado === 'checkout' ? (
        <CheckoutLigado cicloInicial={p.checkout.cicloInicial} perfisIniciais={p.checkout.perfisIniciais} gbIniciais={p.checkout.gbIniciais}
          aoAtivar={p.checkout.aoAtivar}
          aoIrParaPagamento={p.checkout.aoIrParaPagamento} aoJaSerAssinante={p.checkout.aoJaSerAssinante} />
      ) : null}
    />
  );
};

export const AjustarPlanoLigado: FC = () => <AjustarPlano a={useAjustarPlano()} />;
export const PagamentosLigados: FC = () => <Pagamentos p={usePagamentos()} />;
export const PagamentoPixLigado: FC = () => <PagamentoPix x={usePagamentoPix()} />;

/** O desbloqueio de um perfil. O TCLE (que mora fora do ui-carbon) chega pelo contêiner. */
export const DesbloqueioLigado: FC<{ envolverDiagnostico?: (n: ReactNode) => ReactNode }> = ({ envolverDiagnostico }) => {
  const d = useDesbloqueio();
  return (
    <Desbloqueio d={d} envolverDiagnostico={envolverDiagnostico}
      // O rodapé (o código de acesso e o que o PRO inclui) vai na coluna do pagamento, embaixo dele.
      checkout={(rodape) => (d.estado === 'checkout' ? (
        <CheckoutLigado aoAtivar={d.checkout.aoAtivar} aoIrParaPagamento={d.checkout.aoIrParaPagamento} aoJaSerAssinante={d.checkout.aoJaSerAssinante} rodape={rodape} />
      ) : null)} />
  );
};

/** A confirmação da assinatura (/planos/sucesso): leva aos perfis. */
export const SucessoDaAssinaturaLigado: FC = () => {
  const navigate = useNavigate();
  return (
    <Sucesso pictograma={CheckmarkOutline} titulo='Boas-vindas ao Maestra PRO' subtitulo='Seu pagamento foi confirmado.'
      // Os módulos que o PRO libera, pelo nome que têm no menu (04/10/2026). "Todos os recursos da
      // plataforma" e "músicas ilimitadas" prometiam o que a assinatura não entrega: perfis além do
      // primeiro são adicionais, e os arquivos cabem na cota da conta.
      texto='Seu perfil está liberado por inteiro: o Desenvolvimento (Diagnóstico REAL, planejamento e plano de ação), o Controle de shows (vendas e produção), Músicas, Agenda, Equipe e Arquivos, com a Nyta ao seu lado em todos eles.'
      botao='Começar a usar' seguir={() => navigate('/artists', { replace: true })} />
  );
};

export const ModuloTravadoLigado: FC<{ feature: LockedFeatureKey; papel?: PapelNoBloqueio }> = ({ feature, papel }) => (
  <ModuloTravado m={useModuloTravado(feature, papel)} />
);
